/**
 * WellHaus Shipping Progress Bar — v3
 * ===================================
 *
 * REPLACES assets/shipping-progress-bar.js entirely.
 * Select all in the old file, delete, paste this in.
 *
 * CHANGE FROM v2
 * --------------
 * The membership was still counting toward the free-shipping threshold.
 * v2 excluded it by matching a single hardcoded variant ID. That only
 * covers one variant, and it silently fails if the ID ever changes or if
 * the product has more than one variant.
 *
 * v3 excludes it by PRODUCT HANDLE (wellhaus-membership), which covers
 * every variant of the product and survives variant IDs changing. The old
 * variant-ID check is kept as a secondary safety net, plus a URL check.
 *
 * Everything else — copy, box flat rates, live cart sync — is unchanged.
 */
(function () {
  'use strict';

  if (window.__whShippingBarV2) return;
  window.__whShippingBarV2 = true;

  var CONFIG = {
    // Collections that ship flat-rate $15 and do NOT count toward $110.
    boxCollections: [
      'wh-seasonal-box',
      'wh-boxes',
      'clean-collab-hub',
      'wh-holiday-boxes'
    ],

    // Marketplace only — used for the MEMBER threshold math.
    marketplaceCollection: 'wellhaus-marketplace',

    // ---- Membership: never counts toward ANY threshold ----------------
    // Matched by handle first (covers all variants), variant ID second.
    membershipHandles: ['wellhaus-membership'],
    membershipVariantId: 46185482256628,

    // Thresholds, in cents.
    nonMemberThreshold: 11000, // $110
    memberThreshold: 7500,     // $75

    // Displayed flat rates.
    standardFlatRate: '$9.99',
    boxFlatRate: '$15.00',

    // Collection-membership cache.
    cacheKey: 'wh-ship-collections-v2',
    cacheTtlMs: 1000 * 60 * 30, // 30 minutes

    confettiColors: ['#315144', '#798440', '#C5B04E', '#D8AB91', '#A5B8D4']
  };

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------
  function money(cents) {
    var v = (Math.max(0, cents) / 100).toFixed(2);
    return '$' + v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function fetchCollectionIds(handle) {
    var ids = [];

    function page(n) {
      return fetch(
        '/collections/' + handle + '/products.json?limit=250&page=' + n,
        { credentials: 'same-origin', headers: { Accept: 'application/json' } }
      )
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data && data.products) || [];
          products.forEach(function (p) { ids.push(p.id); });
          if (products.length === 250 && n < 10) return page(n + 1);
          return ids;
        })
        .catch(function () { return ids; });
    }

    return page(1);
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CONFIG.cacheKey);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.t > CONFIG.cacheTtlMs) return null;
      return parsed.d;
    } catch (e) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(
        CONFIG.cacheKey,
        JSON.stringify({ t: Date.now(), d: data })
      );
    } catch (e) { /* private mode / storage full — skip caching */ }
  }

  var collectionsPromise = null;

  function getCollections() {
    if (collectionsPromise) return collectionsPromise;

    var cached = readCache();
    if (cached) {
      collectionsPromise = Promise.resolve({
        box: cached.box,
        marketplace: cached.marketplace
      });
      return collectionsPromise;
    }

    var jobs = CONFIG.boxCollections.map(fetchCollectionIds);
    jobs.push(fetchCollectionIds(CONFIG.marketplaceCollection));

    collectionsPromise = Promise.all(jobs).then(function (results) {
      var marketplace = results.pop();
      var box = [];
      results.forEach(function (list) { box = box.concat(list); });
      var data = { box: box, marketplace: marketplace };
      writeCache(data);
      return data;
    });

    return collectionsPromise;
  }

  function toSet(arr) {
    var set = Object.create(null);
    (arr || []).forEach(function (id) { set[id] = true; });
    return set;
  }

  /**
   * True if this cart line is the WellHaus Membership.
   * Handle match is primary; variant ID and URL are backups so the
   * exclusion still holds if any one signal is missing.
   */
  function isMembershipLine(line) {
    if (!line) return false;

    var handle = (line.handle || '').toLowerCase();
    if (handle && CONFIG.membershipHandles.indexOf(handle) > -1) return true;

    if (line.variant_id === CONFIG.membershipVariantId) return true;

    var url = (line.url || '').toLowerCase();
    for (var i = 0; i < CONFIG.membershipHandles.length; i++) {
      if (url.indexOf('/products/' + CONFIG.membershipHandles[i]) > -1) return true;
    }

    return false;
  }

  // -------------------------------------------------------------------
  // Cart math
  // -------------------------------------------------------------------
  function analyse(cart, collections) {
    var boxSet = toSet(collections.box);
    var marketSet = toSet(collections.marketplace);

    var items = (cart && cart.items) || [];
    var hasMembershipInCart = false;
    var nonMemberQualifying = 0; // everything that isn't a box
    var memberQualifying = 0;    // marketplace only

    items.forEach(function (line) {
      // Membership is a digital item: it marks the shopper as a member
      // but never contributes to any shipping threshold.
      if (isMembershipLine(line)) {
        hasMembershipInCart = true;
        return;
      }

      var price =
        typeof line.final_line_price === 'number'
          ? line.final_line_price
          : line.line_price || 0;

      if (!boxSet[line.product_id]) nonMemberQualifying += price;
      if (marketSet[line.product_id]) memberQualifying += price;
    });

    return {
      hasMembershipInCart: hasMembershipInCart,
      nonMemberQualifying: nonMemberQualifying,
      memberQualifying: memberQualifying
    };
  }

  // -------------------------------------------------------------------
  // Copy
  // -------------------------------------------------------------------
  function buildText(isMember, isBox, qualifying, threshold) {
    var remaining = threshold - qualifying;

    if (remaining <= 0) {
      return isMember
        ? 'Free shipping unlocked!'
        : 'Free shipping unlocked on your Marketplace &amp; Merch order!';
    }

    var away = '<span class="wh-ship-amt">' + money(remaining) + '</span>';

    if (isMember) {
      if (isBox) {
        return (
          'This box ships free &middot; Marketplace orders ship free at $75 ' +
          '&mdash; you&rsquo;re ' + away + ' away'
        );
      }
      return (
        'Boxes &amp; Merch always ship free &middot; Marketplace orders ship free at $75 ' +
        '&mdash; you&rsquo;re ' + away + ' away'
      );
    }

    var rate = isBox ? CONFIG.boxFlatRate : CONFIG.standardFlatRate;

    return (
      rate + ' flat-rate shipping &middot; Marketplace &amp; Merch items ship free at $110 ' +
      '&mdash; you&rsquo;re ' + away + ' away'
    );
  }

  function buildNote(isMember, isBox) {
    if (!isBox) return '';
    if (isMember) return '';
    return (
      'This box ships flat-rate at ' + CONFIG.boxFlatRate +
      ' and doesn\u2019t count toward the $110 threshold.'
    );
  }

  // -------------------------------------------------------------------
  // Confetti
  // -------------------------------------------------------------------
  function fireConfetti(host) {
    if (!host) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    for (var i = 0; i < 18; i++) {
      (function (n) {
        var piece = document.createElement('span');
        piece.className = 'wh-ship-confetti-piece';
        piece.style.left = (10 + Math.random() * 80) + '%';
        piece.style.background =
          CONFIG.confettiColors[n % CONFIG.confettiColors.length];
        host.appendChild(piece);

        var driftX = (Math.random() - 0.5) * 120;
        var riseY = -(30 + Math.random() * 60);
        var spin = (Math.random() - 0.5) * 540;

        requestAnimationFrame(function () {
          piece.animate(
            [
              { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
              {
                transform:
                  'translate(' + driftX + 'px, ' + riseY + 'px) rotate(' + spin + 'deg)',
                opacity: 0
              }
            ],
            { duration: 900 + Math.random() * 400, easing: 'cubic-bezier(.2,.7,.3,1)' }
          ).onfinish = function () {
            if (piece.parentNode) piece.parentNode.removeChild(piece);
          };
        });
      })(i);
    }
  }

  // -------------------------------------------------------------------
  // Bar instance
  // -------------------------------------------------------------------
  function ShippingBar(root) {
    this.root = root;
    this.textEl = root.querySelector('[data-wh-ship-text]');
    this.noteEl = root.querySelector('[data-wh-ship-note]');
    this.fillEl = root.querySelector('[data-wh-ship-fill]');
    this.confettiEl = root.querySelector('[data-wh-ship-confetti]');
    this.toggleEl = root.querySelector('[data-wh-ship-toggle]');
    this.detailsEl = root.querySelector('[data-wh-ship-details]');

    this.isBox = root.getAttribute('data-wh-is-box') === 'true';
    this.hasMemberTag = root.getAttribute('data-wh-member-tag') === 'true';
    this.wasUnlocked = null;

    this.bindAccordion();
  }

  ShippingBar.prototype.bindAccordion = function () {
    var self = this;
    if (!this.toggleEl || !this.detailsEl) return;

    this.toggleEl.addEventListener('click', function () {
      var open = self.toggleEl.getAttribute('aria-expanded') === 'true';
      self.toggleEl.setAttribute('aria-expanded', open ? 'false' : 'true');
      self.detailsEl.style.display = open ? 'none' : 'block';
    });
  };

  ShippingBar.prototype.render = function (state) {
    var isMember = this.hasMemberTag || state.hasMembershipInCart;

    var threshold = isMember ? CONFIG.memberThreshold : CONFIG.nonMemberThreshold;
    var qualifying = isMember ? state.memberQualifying : state.nonMemberQualifying;

    if (this.textEl) {
      this.textEl.innerHTML = buildText(isMember, this.isBox, qualifying, threshold);
    }

    if (this.noteEl) {
      var note = buildNote(isMember, this.isBox);
      this.noteEl.innerHTML = note;
      this.noteEl.style.display = note ? 'block' : 'none';
    }

    // Fill — ALWAYS a percentage string, never px.
    var pct = threshold > 0 ? (qualifying / threshold) * 100 : 0;
    if (pct > 100) pct = 100;
    if (pct < 0) pct = 0;
    if (this.fillEl) this.fillEl.style.width = pct + '%';

    var unlocked = qualifying >= threshold;
    if (this.wasUnlocked === false && unlocked) this.celebrate();
    this.wasUnlocked = unlocked;
  };

  ShippingBar.prototype.celebrate = function () {
    var self = this;
    this.root.classList.add('wh-shipping-bar--celebrating');
    fireConfetti(this.confettiEl);
    setTimeout(function () {
      self.root.classList.remove('wh-shipping-bar--celebrating');
    }, 1400);
  };

  // -------------------------------------------------------------------
  // Cart request interception
  // -------------------------------------------------------------------
  function watchCartRequests(onChange) {
    var CART_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;

    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function () {
        var url = '';
        try {
          url = typeof arguments[0] === 'string'
            ? arguments[0]
            : (arguments[0] && arguments[0].url) || '';
        } catch (e) { /* noop */ }

        var promise = origFetch.apply(this, arguments);

        if (CART_RE.test(url)) {
          promise.then(function () { setTimeout(onChange, 80); })
                 .catch(function () { /* noop */ });
        }
        return promise;
      };
    }

    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__whCartUrl = url || '';
      return origOpen.apply(this, arguments);
    };

    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (CART_RE.test(this.__whCartUrl || '')) {
        this.addEventListener('loadend', function () {
          setTimeout(onChange, 80);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  // -------------------------------------------------------------------
  // Init
  // -------------------------------------------------------------------
  function init() {
    var roots = document.querySelectorAll('.wh-shipping-bar');
    if (!roots.length) return;

    var bars = Array.prototype.map.call(roots, function (root) {
      return new ShippingBar(root);
    });

    var refreshing = false;
    var queued = false;

    function refresh() {
      if (refreshing) { queued = true; return; }
      refreshing = true;

      Promise.all([fetchCart(), getCollections()])
        .then(function (results) {
          var state = analyse(results[0], results[1]);
          bars.forEach(function (bar) { bar.render(state); });
        })
        .catch(function () { /* leave last good state on screen */ })
        .then(function () {
          refreshing = false;
          if (queued) { queued = false; refresh(); }
        });
    }

    refresh();

    watchCartRequests(refresh);

    [
      'cart:refresh',
      'cart:updated',
      'cart:change',
      'cart:build',
      'ajaxCart.afterCartLoad',
      'loop:cart:updated'
    ].forEach(function (evt) {
      document.addEventListener(evt, refresh);
      window.addEventListener(evt, refresh);
    });

    document.addEventListener('cart-update', refresh);

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) refresh();
    });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) refresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();